import { URL } from "../constants";
import { apiSlice } from "./apiSlice";

export const bomApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createBlankBom: builder.mutation({
      /**
       * 创建包含一条空白物料的 BOM 任务。
       * @param {{sessionId: string}} params 创建参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ sessionId }) => ({
        url: `${URL}/bom`,
        method: "POST",
        body: { sessionId },
      }),
      invalidatesTags: ["Bom"],
    }),

    createManualBom: builder.mutation({
      /**
       * 根据逐行输入的料号和数量创建 BOM 任务。
       * @param {{sessionId: string, input: string}} params 手动配单参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ sessionId, input }) => ({
        url: `${URL}/bom/manual-input`,
        method: "POST",
        body: { sessionId, input },
      }),
      invalidatesTags: ["Bom"],
    }),

    createClipboardBom: builder.mutation({
      /**
       * 根据 Excel/WPS 剪贴板二维表格创建 BOM 任务。
       * @param {{sessionId: string, input: string}} params 剪贴板建表参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ sessionId, input }) => ({
        url: `${URL}/bom/clipboard`,
        method: "POST",
        body: { sessionId, input },
      }),
      invalidatesTags: ["Bom"],
    }),

    reparseBom: builder.mutation({
      /**
       * 按用户指定的表头行和字段映射重新解析 BOM。
       * @param {{bomId: string, sessionId: string, headerRowIndex: number, fieldMap: Object}} params 映射参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, headerRowIndex, fieldMap }) => ({
        url: `${URL}/bom/${bomId}/reparse`,
        method: "POST",
        body: { sessionId, headerRowIndex, fieldMap },
      }),
      invalidatesTags: ["Bom"],
    }),

    uploadBom: builder.mutation({
      /**
       * 上传 BOM 文件并解析匹配。
       * @param {FormData} formData 上传表单
       * @returns {{url: string, method: string, body: FormData}}
       */
      query: (formData) => ({
        url: `${URL}/bom/upload`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Bom"],
    }),

    getBomOrder: builder.query({
      /**
       * 获取 BOM 任务详情。
       * @param {{id: string, sessionId: string}} params 查询参数
       * @returns {{url: string, method: string, params: Object}}
       */
      query: ({ id, sessionId }) => ({
        url: `${URL}/bom/${id}`,
        method: "GET",
        params: sessionId ? { sessionId } : undefined,
      }),
      providesTags: ["Bom"],
    }),

    getBomOrderHistory: builder.query({
      /**
       * 分页获取当前账号或游客会话的 BOM 历史摘要。
       * @param {{sessionId: string, page?: number, limit?: number, keyword?: string}} params 查询参数
       * @returns {{url: string, method: string, params: Object}}
       */
      query: ({ sessionId, page = 1, limit = 10, keyword = "" }) => ({
        url: `${URL}/bom/history`,
        method: "GET",
        params: { sessionId, page, limit, keyword: keyword || undefined },
      }),
      providesTags: ["Bom"],
    }),

    getBomRelatedOrders: builder.query({
      /**
       * 获取当前用户在指定 BOM 下创建的订单摘要。
       * @param {{id: string, sessionId: string}} params 查询参数
       * @returns {{url: string, method: string, params: Object}}
       */
      query: ({ id, sessionId }) => ({
        url: `${URL}/bom/${id}/orders`,
        method: "GET",
        params: sessionId ? { sessionId } : undefined,
      }),
      providesTags: ["Bom", "Order"],
    }),

    createBomItem: builder.mutation({
      /**
       * 在 BOM 云表末尾新增一条物料。
       * @param {{bomId: string, sessionId: string, normalized?: Object}} params 新增参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, normalized }) => ({
        url: `${URL}/bom/${bomId}/items`,
        method: "POST",
        body: { sessionId, normalized },
      }),
      invalidatesTags: ["Bom"],
    }),

    deleteBomItems: builder.mutation({
      /**
       * 删除 BOM 云表中选中的物料行。
       * @param {{bomId: string, sessionId: string, itemIds: string[]}} params 删除参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/items`,
        method: "DELETE",
        body: { sessionId, itemIds },
      }),
      invalidatesTags: ["Bom"],
    }),

    updateBomItem: builder.mutation({
      /**
       * 更新 BOM 明细数量、选择状态或匹配商品。
       * @param {{bomId: string, itemId: string, sessionId: string, payload: Object}} params 更新参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, itemId, sessionId, payload }) => ({
        url: `${URL}/bom/${bomId}/items/${itemId}`,
        method: "PATCH",
        body: {
          ...payload,
          sessionId,
        },
      }),
      invalidatesTags: ["Bom"],
    }),

    searchBomItemCandidates: builder.mutation({
      /**
       * 搜索单条 BOM 的可替换商品，并返回字段差异与匹配评分。
       * @param {{bomId: string, itemId: string, sessionId: string, keyword?: string}} params 搜索参数
       * @returns {{url: string, method: string, params: Object}}
       */
      query: ({ bomId, itemId, sessionId, keyword }) => ({
        url: `${URL}/bom/${bomId}/items/${itemId}/candidates`,
        method: "GET",
        params: { sessionId, keyword },
      }),
    }),

    updateBomItems: builder.mutation({
      /**
       * 在一次请求中批量更新 BOM 明细。
       * @param {{bomId: string, sessionId: string, patches: Object[]}} params 批量更新参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, patches }) => ({
        url: `${URL}/bom/${bomId}/items`,
        method: "PATCH",
        body: { sessionId, patches },
      }),
      invalidatesTags: ["Bom"],
    }),

    rematchBom: builder.mutation({
      /**
       * 重新匹配 BOM 任务。
       * @param {{bomId: string, sessionId: string}} params 重新匹配参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId }) => ({
        url: `${URL}/bom/${bomId}/rematch`,
        method: "POST",
        body: { sessionId },
      }),
      invalidatesTags: ["Bom"],
    }),

    aiRematchBom: builder.mutation({
      /**
       * 使用 AI 为无法匹配的 BOM 行生成待确认候选。
       * @param {{bomId: string, sessionId: string, itemIds?: string[]}} params AI 补配参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/ai-rematch`,
        method: "POST",
        body: { sessionId, itemIds },
      }),
      invalidatesTags: ["Bom"],
    }),

    reviewBomAiSuggestion: builder.mutation({
      /**
       * 接受或拒绝单条 AI 补配结果。
       * @param {{bomId: string, itemId: string, sessionId: string, decision: string}} params 审核参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, itemId, sessionId, decision }) => ({
        url: `${URL}/bom/${bomId}/items/${itemId}/ai-review`,
        method: "POST",
        body: { sessionId, decision },
      }),
      invalidatesTags: ["Bom"],
    }),

    buildBomCartItems: builder.mutation({
      /**
       * 获取可写入购物车的 BOM 商品项。
       * @param {{bomId: string, sessionId: string, itemIds: string[]}} params 加购参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/cart-items`,
        method: "POST",
        body: { sessionId, itemIds },
      }),
    }),

    previewBomPurchase: builder.mutation({
      /**
       * 预览已选 BOM 的合并数量、MOQ、阶梯价和最新库存。
       * @param {{bomId: string, sessionId: string, itemIds: string[]}} params 预览参数
       * @returns {{url: string, method: string, body: Object}}
       */
      query: ({ bomId, sessionId, itemIds }) => ({
        url: `${URL}/bom/${bomId}/purchase-preview`,
        method: "POST",
        body: { sessionId, itemIds },
      }),
    }),
  }),
});

export const {
  useCreateBlankBomMutation,
  useCreateClipboardBomMutation,
  useCreateManualBomMutation,
  useReparseBomMutation,
  useUploadBomMutation,
  useGetBomOrderQuery,
  useGetBomOrderHistoryQuery,
  useGetBomRelatedOrdersQuery,
  useCreateBomItemMutation,
  useDeleteBomItemsMutation,
  useUpdateBomItemMutation,
  useSearchBomItemCandidatesMutation,
  useUpdateBomItemsMutation,
  useRematchBomMutation,
  useAiRematchBomMutation,
  useReviewBomAiSuggestionMutation,
  useBuildBomCartItemsMutation,
  usePreviewBomPurchaseMutation,
} = bomApi;
